import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { SubmitHandler, DefaultValues, useForm } from 'react-hook-form';
import AppPasswordTextField from '../../common/components/form-inputs/AppPasswordTextField';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import AuthContainer from './components/AuthContainer';
import { useLoginMutation } from '../../api/authApiSlice';
import { useLazyGetMeQuery } from '../../api/userApiSlice';
import { MenuEnum } from '../../api/enum/common.enum';
import { useDispatch } from 'react-redux';
import { setCredentials, setJustLoggedIn } from './authSlice';
import { EmailLoginDTO } from '../../api/DTO/auth.interface';
import AppTextField from '../../common/components/form-inputs/AppTextField';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Link from '@mui/material/Link';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import { Box, IconButton } from '@mui/material';
import AuthCard from './components/AuthCard';

export const defaultValues: DefaultValues<EmailLoginDTO> = {
	username: '',
	password: '',
};

export default function LoginPage() {
	const { t } = useTranslation();

	const formSchema = Yup.object().shape({
		username: Yup.string().required(t('errorMessage.fieldRequired')),
		password: Yup.string().required(t('errorMessage.fieldRequired')),
	});

	const { handleSubmit, control } = useForm<EmailLoginDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const navigate = useNavigate();
	const location = useLocation();
	const from = (location.state?.from.pathname as string) || '/';
	const fromSearchParams = (location.state?.from.search as string) || '';
	const [login] = useLoginMutation();
	const [getMe] = useLazyGetMeQuery();
	const dispatch = useDispatch();

	const resolveDefaultRoute = (menuList?: MenuEnum[]) => {
		if (menuList?.includes(MenuEnum.MemberSummary)) {
			return '/member-management/member-list';
		}
		if (menuList?.includes(MenuEnum.SystemUserManagement)) {
			return '/user-management/user-management-list';
		}
		return '/';
	};

	const onSubmit: SubmitHandler<EmailLoginDTO> = async (formResult) => {
		try {
			const loginResponse = await login(formResult).unwrap();
			dispatch(
				setCredentials({
					accessToken: loginResponse.data.accessToken,
				})
			);
			dispatch(setJustLoggedIn());
			if (from == '/') {
				const meResponse = await getMe(null).unwrap();
				navigate(resolveDefaultRoute(meResponse.data.menuList));
			} else {
				navigate(from + fromSearchParams);
			}
		} catch (err) {
			console.log('error');
		}
	};

	return (
		<AuthContainer>
			<AuthCard title={t('common.accountLogin')} message={''}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Stack alignItems='center' spacing={2} marginTop='24px'>
						<AppTextField
							control={control}
							name='username'
							label={t('common.userId') + '*'}
						/>

						<AppPasswordTextField
							control={control}
							name='password'
							label={t('common.password') + '*'}
						/>
					</Stack>
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<Button
							fullWidth
							type='submit'
							variant='contained'
							sx={{
								width: '80px',
								marginTop: 3,
							}}
						>
							{t('button.signIn')}
						</Button>
					</Box>
				</form>
			</AuthCard>
		</AuthContainer>
	);
}
